import React, { useState, useMemo } from 'react';
import { Search, AlertTriangle, ArrowRight, ChevronDown, ChevronRight, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Meta } from '../../components/Meta';

interface NutrientInfo {
  name: string;
  type: 'vitamin' | 'mineral';
  benefits: string[];
  deficiency: string[];
  toxicity: string[];
  sources: string[];
  rda?: string;
  absorption: string[];
  notes?: string;
}

const nutrients: NutrientInfo[] = [
  {
    "name": "Vitamin A",
    "type": "vitamin",
    "benefits": [
      "Essential for vision",
      "Supports immune system",
      "Maintains healthy skin",
      "Promotes cell growth"
    ],
    "deficiency": [
      "Night blindness",
      "Dry eyes",
      "Weakened immune system",
      "Poor skin health"
    ],
    "toxicity": [
      "Nausea",
      "Blurred vision",
      "Bone pain",
      "Skin irritation"
    ],
    "sources": [
      "Sweet potatoes",
      "Carrots",
      "Spinach",
      "Eggs",
      "Liver"
    ],
    "absorption": [
      "Consume with healthy fats (oils, nuts, avocado)",
      "Pair with vitamin D-rich foods",
      "Cook orange/yellow vegetables to enhance absorption",
      "Avoid excessive alcohol which impairs absorption"
    ],
    "rda": "900 mcg (men), 700 mcg (women)"
  },
  {
    "name": "Vitamin B1 (Thiamine)",
    "type": "vitamin",
    "benefits": [
      "Converts food into energy",
      "Supports nerve function",
      "Promotes heart health"
    ],
    "deficiency": [
      "Fatigue",
      "Muscle weakness",
      "Nerve damage",
      "Beriberi"
    ],
    "toxicity": [
      "Headaches",
      "Insomnia",
      "Allergic reactions"
    ],
    "sources": [
      "Whole grains",
      "Pork",
      "Beans",
      "Nuts",
      "Seeds"
    ],
    "absorption": [
      "Consume with other B vitamins",
      "Avoid excessive alcohol"
    ],
    "rda": "1.2 mg (men), 1.1 mg (women)"
  },
  {
    "name": "Vitamin B2 (Riboflavin)",
    "type": "vitamin",
    "benefits": [
      "Energy production",
      "Skin health",
      "Antioxidant protection"
    ],
    "deficiency": [
      "Cracked lips",
      "Sore throat",
      "Skin disorders",
      "Anemia"
    ],
    "toxicity": [
      "Itching",
      "Numbness"
    ],
    "sources": [
      "Dairy products",
      "Eggs",
      "Lean meats",
      "Green leafy vegetables"
    ],
    "absorption": [
      "Consume with other B vitamins",
      "Avoid exposure to light (destroys riboflavin)"
    ],
    "rda": "1.3 mg (men), 1.1 mg (women)"
  },
  {
    "name": "Vitamin B3 (Niacin)",
    "type": "vitamin",
    "benefits": [
      "Supports digestion",
      "Skin health",
      "Nervous system function"
    ],
    "deficiency": [
      "Pellagra (diarrhea, dermatitis, dementia)",
      "Fatigue",
      "Depression"
    ],
    "toxicity": [
      "Flushing",
      "Liver damage",
      "High blood sugar"
    ],
    "sources": [
      "Chicken",
      "Tuna",
      "Peanuts",
      "Whole grains",
      "Mushrooms"
    ],
    "absorption": [
      "Take with food to reduce flushing",
      "Avoid excessive alcohol"
    ],
    "rda": "16 mg (men), 14 mg (women)"
  },
  {
    "name": "Vitamin B5 (Pantothenic Acid)",
    "type": "vitamin",
    "benefits": [
      "Energy production",
      "Hormone synthesis",
      "Skin health"
    ],
    "deficiency": [
      "Fatigue",
      "Insomnia",
      "Numbness",
      "Digestive issues"
    ],
    "toxicity": [
      "Diarrhea"
    ],
    "sources": [
      "Meat",
      "Whole grains",
      "Avocados",
      "Broccoli",
      "Mushrooms"
    ],
    "absorption": [
      "Consume with other B vitamins",
      "Avoid overcooking foods"
    ],
    "rda": "5 mg (adults)"
  },
  {
    "name": "Vitamin B6 (Pyridoxine)",
    "type": "vitamin",
    "benefits": [
      "Brain development",
      "Immune function",
      "Hemoglobin production"
    ],
    "deficiency": [
      "Anemia",
      "Depression",
      "Weakened immune system",
      "Skin rashes"
    ],
    "toxicity": [
      "Nerve damage",
      "Numbness",
      "Difficulty walking"
    ],
    "sources": [
      "Chickpeas",
      "Salmon",
      "Potatoes",
      "Bananas",
      "Poultry"
    ],
    "absorption": [
      "Consume with magnesium",
      "Avoid excessive supplementation"
    ],
    "rda": "1.3–1.7 mg (adults)"
  },
  {
    "name": "Vitamin B7 (Biotin)",
    "type": "vitamin",
    "benefits": [
      "Hair, skin, and nail health",
      "Metabolism support"
    ],
    "deficiency": [
      "Hair loss",
      "Skin rashes",
      "Brittle nails",
      "Fatigue"
    ],
    "toxicity": [
      "Acne",
      "Allergic reactions"
    ],
    "sources": [
      "Eggs",
      "Almonds",
      "Sweet potatoes",
      "Spinach",
      "Salmon"
    ],
    "absorption": [
      "Consume with other B vitamins",
      "Avoid raw egg whites (inhibit absorption)"
    ],
    "rda": "30 mcg (adults)"
  },
  {
    "name": "Vitamin B9 (Folate/Folic Acid)",
    "type": "vitamin",
    "benefits": [
      "DNA synthesis",
      "Red blood cell formation",
      "Fetal development"
    ],
    "deficiency": [
      "Anemia",
      "Birth defects",
      "Fatigue",
      "Poor growth"
    ],
    "toxicity": [
      "May mask vitamin B12 deficiency",
      "Nausea"
    ],
    "sources": [
      "Leafy greens",
      "Legumes",
      "Citrus fruits",
      "Fortified grains"
    ],
    "absorption": [
      "Consume with vitamin B12",
      "Avoid excessive alcohol"
    ],
    "rda": "400 mcg (adults), 600 mcg (pregnant women)"
  },
  {
    "name": "Vitamin B12 (Cobalamin)",
    "type": "vitamin",
    "benefits": [
      "Nerve function",
      "Red blood cell production",
      "DNA synthesis"
    ],
    "deficiency": [
      "Anemia",
      "Fatigue",
      "Nerve damage",
      "Memory loss"
    ],
    "toxicity": [
      "Acne",
      "Allergic reactions"
    ],
    "sources": [
      "Meat",
      "Fish",
      "Dairy",
      "Eggs",
      "Fortified cereals"
    ],
    "absorption": [
      "Consume with folate",
      "Avoid antacids (reduce absorption)"
    ],
    "rda": "2.4 mcg (adults)"
  },
  {
    "name": "Vitamin C",
    "type": "vitamin",
    "benefits": [
      "Antioxidant protection",
      "Collagen production",
      "Immune system support",
      "Iron absorption"
    ],
    "deficiency": [
      "Weakened immune system",
      "Slow wound healing",
      "Bleeding gums",
      "Fatigue"
    ],
    "toxicity": [
      "Diarrhea",
      "Nausea",
      "Abdominal cramps",
      "Kidney stones"
    ],
    "sources": [
      "Citrus fruits",
      "Bell peppers",
      "Strawberries",
      "Broccoli",
      "Kiwi"
    ],
    "absorption": [
      "Consume raw or minimally cooked foods",
      "Take on an empty stomach",
      "Avoid consuming with caffeine",
      "Space out doses throughout the day"
    ],
    "rda": "90 mg (men), 75 mg (women)"
  },
  {
    "name": "Vitamin D",
    "type": "vitamin",
    "benefits": [
      "Bone health",
      "Immune function",
      "Calcium absorption"
    ],
    "deficiency": [
      "Weak bones",
      "Rickets",
      "Fatigue",
      "Depression"
    ],
    "toxicity": [
      "Hypercalcemia (high calcium levels)",
      "Kidney damage"
    ],
    "sources": [
      "Sunlight",
      "Fatty fish",
      "Fortified milk",
      "Egg yolks"
    ],
    "absorption": [
      "Consume with healthy fats",
      "Get sunlight exposure"
    ],
    "rda": "600–800 IU (adults)"
  },
  {
    "name": "Vitamin E",
    "type": "vitamin",
    "benefits": [
      "Antioxidant protection",
      "Skin health",
      "Immune support"
    ],
    "deficiency": [
      "Nerve damage",
      "Muscle weakness",
      "Vision problems"
    ],
    "toxicity": [
      "Bleeding",
      "Nausea",
      "Diarrhea"
    ],
    "sources": [
      "Nuts",
      "Seeds",
      "Spinach",
      "Vegetable oils",
      "Avocados"
    ],
    "absorption": [
      "Consume with healthy fats",
      "Avoid high doses"
    ],
    "rda": "15 mg (adults)"
  },
  {
    "name": "Vitamin K",
    "type": "vitamin",
    "benefits": [
      "Blood clotting",
      "Bone health",
      "Heart health"
    ],
    "deficiency": [
      "Excessive bleeding",
      "Weak bones",
      "Bruising"
    ],
    "toxicity": [
      "May interfere with blood thinners"
    ],
    "sources": [
      "Leafy greens",
      "Broccoli",
      "Brussels sprouts",
      "Fish",
      "Meat"
    ],
    "absorption": [
      "Consume with healthy fats",
      "Avoid excessive vitamin A"
    ],
    "rda": "120 mcg (men), 90 mcg (women)"
  },
  {
    "name": "Calcium",
    "type": "mineral",
    "benefits": [
      "Bone health",
      "Muscle function",
      "Nerve signaling"
    ],
    "deficiency": [
      "Weak bones",
      "Osteoporosis",
      "Muscle cramps"
    ],
    "toxicity": [
      "Kidney stones",
      "Constipation",
      "Impaired iron absorption"
    ],
    "sources": [
      "Dairy products",
      "Leafy greens",
      "Fortified plant-based milk",
      "Almonds"
    ],
    "absorption": [
      "Consume with vitamin D",
      "Avoid excessive phosphorus"
    ],
    "rda": "1000–1200 mg (adults)"
  },
  {
    "name": "Iron",
    "type": "mineral",
    "benefits": [
      "Oxygen transport",
      "Energy production",
      "Immune function"
    ],
    "deficiency": [
      "Fatigue",
      "Weakness",
      "Pale skin",
      "Shortness of breath"
    ],
    "toxicity": [
      "Stomach pain",
      "Constipation",
      "Organ damage"
    ],
    "sources": [
      "Red meat",
      "Spinach",
      "Lentils",
      "Oysters",
      "Quinoa"
    ],
    "absorption": [
      "Pair with vitamin C-rich foods",
      "Avoid coffee/tea within 1 hour of iron-rich meals",
      "Cook in cast iron cookware",
      "Take on an empty stomach if supplementing"
    ],
    "rda": "8 mg (men), 18 mg (women)"
  },
  {
    "name": "Magnesium",
    "type": "mineral",
    "benefits": [
      "Muscle function",
      "Nerve function",
      "Bone health",
      "Energy production"
    ],
    "deficiency": [
      "Muscle cramps",
      "Fatigue",
      "Anxiety",
      "Irregular heartbeat"
    ],
    "toxicity": [
      "Diarrhea",
      "Nausea",
      "Low blood pressure"
    ],
    "sources": [
      "Almonds",
      "Spinach",
      "Black beans",
      "Avocado",
      "Dark chocolate"
    ],
    "absorption": [
      "Take with vitamin D and B6",
      "Consume with healthy fats",
      "Avoid with high-calcium foods",
      "Best absorbed in smaller doses throughout the day"
    ],
    "rda": "400–420 mg (men), 310–320 mg (women)"
  },
  {
    "name": "Zinc",
    "type": "mineral",
    "benefits": [
      "Immune function",
      "Wound healing",
      "DNA synthesis"
    ],
    "deficiency": [
      "Weak immune system",
      "Hair loss",
      "Slow wound healing"
    ],
    "toxicity": [
      "Nausea",
      "Vomiting",
      "Weakened immune function"
    ],
    "sources": [
      "Meat",
      "Shellfish",
      "Legumes",
      "Seeds",
      "Nuts"
    ],
    "absorption": [
      "Consume with protein",
      "Avoid high doses"
    ],
    "rda": "11 mg (men), 8 mg (women)"
  },
  {
    "name": "Potassium",
    "type": "mineral",
    "benefits": [
      "Muscle function",
      "Nerve signaling",
      "Blood pressure regulation"
    ],
    "deficiency": [
      "Muscle weakness",
      "Cramps",
      "Irregular heartbeat"
    ],
    "toxicity": [
      "Heart arrhythmias",
      "Muscle weakness"
    ],
    "sources": [
      "Bananas",
      "Potatoes",
      "Spinach",
      "Avocados",
      "Beans"
    ],
    "absorption": [
      "Consume with magnesium",
      "Avoid excessive sodium"
    ],
    "rda": "3400 mg (men), 2600 mg (women)"
  },
  {
    "name": "Sodium",
    "type": "mineral",
    "benefits": [
      "Fluid balance",
      "Nerve signaling",
      "Muscle function"
    ],
    "deficiency": [
      "Hyponatremia (low sodium)",
      "Headaches",
      "Fatigue"
    ],
    "toxicity": [
      "High blood pressure",
      "Heart disease",
      "Kidney damage"
    ],
    "sources": [
      "Table salt",
      "Processed foods",
      "Canned soups"
    ],
    "absorption": [
      "Balance with potassium",
      "Avoid excessive intake"
    ],
    "rda": "<2300 mg (adults)"
  },
  {
    "name": "Phosphorus",
    "type": "mineral",
    "benefits": [
      "Bone health",
      "Energy production",
      "DNA synthesis"
    ],
    "deficiency": [
      "Weak bones",
      "Fatigue",
      "Loss of appetite"
    ],
    "toxicity": [
      "Kidney damage",
      "Calcium imbalance"
    ],
    "sources": [
      "Dairy products",
      "Meat",
      "Fish",
      "Nuts",
      "Seeds"
    ],
    "absorption": [
      "Consume with calcium",
      "Avoid excessive intake"
    ],
    "rda": "700 mg (adults)"
  },
  {
    "name": "Selenium",
    "type": "mineral",
    "benefits": [
      "Antioxidant protection",
      "Thyroid function",
      "Immune support"
    ],
    "deficiency": [
      "Weak immune system",
      "Hair loss",
      "Fatigue"
    ],
    "toxicity": [
      "Hair loss",
      "Nausea",
      "Nerve damage"
    ],
    "sources": [
      "Brazil nuts",
      "Seafood",
      "Meat",
      "Eggs",
      "Whole grains"
    ],
    "absorption": [
      "Consume with vitamin E",
      "Avoid high doses"
    ],
    "rda": "55 mcg (adults)"
  },
  {
    "name": "Iodine",
    "type": "mineral",
    "benefits": [
      "Thyroid function",
      "Metabolism regulation"
    ],
    "deficiency": [
      "Goiter",
      "Hypothyroidism",
      "Fatigue"
    ],
    "toxicity": [
      "Thyroid dysfunction",
      "Nausea",
      "Vomiting"
    ],
    "sources": [
      "Iodized salt",
      "Seafood",
      "Dairy products",
      "Seaweed"
    ],
    "absorption": [
      "Consume with selenium",
      "Avoid excessive intake"
    ],
    "rda": "150 mcg (adults)"
  },
  {
    "name": "Copper",
    "type": "mineral",
    "benefits": [
      "Iron metabolism",
      "Nerve function",
      "Antioxidant protection"
    ],
    "deficiency": [
      "Anemia",
      "Weak bones",
      "Fatigue"
    ],
    "toxicity": [
      "Nausea",
      "Liver damage",
      "Neurological issues"
    ],
    "sources": [
      "Shellfish",
      "Nuts",
      "Seeds",
      "Whole grains",
      "Dark chocolate"
    ],
    "absorption": [
      "Consume with zinc",
      "Avoid excessive supplementation"
    ],
    "rda": "900 mcg (adults)"
  },
  {
    "name": "Manganese",
    "type": "mineral",
    "benefits": [
      "Bone health",
      "Metabolism",
      "Antioxidant protection"
    ],
    "deficiency": [
      "Weak bones",
      "Poor growth",
      "Skin rashes"
    ],
    "toxicity": [
      "Neurological issues",
      "Muscle pain"
    ],
    "sources": [
      "Nuts",
      "Whole grains",
      "Leafy greens",
      "Tea"
    ],
    "absorption": [
      "Consume with iron",
      "Avoid high doses"
    ],
    "rda": "2.3 mg (men), 1.8 mg (women)"
  },
  {
    "name": "Chromium",
    "type": "mineral",
    "benefits": [
      "Blood sugar regulation",
      "Metabolism support"
    ],
    "deficiency": [
      "Impaired glucose tolerance",
      "Fatigue"
    ],
    "toxicity": [
      "Stomach issues"
    ],
    "sources": [
      "Broccoli",
      "Whole grains",
      "Nuts",
      "Meat"
    ],
    "absorption": [
      "Consume with vitamin C",
      "Avoid refined sugars"
    ],
    "rda": "35 mcg (men), 25 mcg (women)"
  },
  {
    "name": "Fluoride",
    "type": "mineral",
    "benefits": [
      "Dental health",
      "Bone strength"
    ],
    "deficiency": [
      "Tooth decay",
      "Weak bones"
    ],
    "toxicity": [
      "Dental fluorosis",
      "Bone fractures"
    ],
    "sources": [
      "Fluoridated water",
      "Tea",
      "Seafood"
    ],
    "absorption": [
      "Consume with calcium",
      "Avoid excessive intake"
    ],
    "rda": "3–4 mg (adults)"
  }
];

function matchesSearch(nutrient: NutrientInfo, term: string): boolean {
  const trimmed = term.trim();
  if (!trimmed) return true;
  const words = trimmed.toLowerCase().split(/\s+/);
  const searchable = [
    nutrient.name,
    ...nutrient.benefits,
    ...nutrient.deficiency,
    ...nutrient.toxicity,
    ...nutrient.sources,
    ...nutrient.absorption,
    nutrient.notes
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return words.every(word => searchable.includes(word));
}

function NutrientCard({ nutrient }: { nutrient: NutrientInfo }) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    benefits: false,
    deficiency: false,
    toxicity: false,
    absorption: false,
    sources: false,
    notes: false
  });

  const toggle = (key: keyof typeof expanded) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const Section = ({
    id,
    title,
    children,
    defaultOpen = false
  }: {
    id: keyof typeof expanded;
    title: string;
    children: React.ReactNode;
    defaultOpen?: boolean;
  }) => {
    const isOpen = expanded[id] ?? defaultOpen;
    return (
      <div className="border-b border-gray-100 last:border-0">
        <button
          type="button"
          onClick={() => toggle(id)}
          className="w-full flex items-center justify-between py-3 text-left hover:bg-gray-50/50 rounded-lg px-1 -mx-1 transition-colors"
        >
          <h3 className="text-sm font-medium text-content">{title}</h3>
          {isOpen ? (
            <ChevronDown className="h-4 w-4 text-content/50" />
          ) : (
            <ChevronRight className="h-4 w-4 text-content/50" />
          )}
        </button>
        {isOpen && <div className="pb-3 pl-1">{children}</div>}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-content">{nutrient.name}</h2>
          <span className="px-2.5 py-1 text-xs font-medium bg-accent-base/20 text-accent-text rounded-full capitalize">
            {nutrient.type}
          </span>
        </div>

        {nutrient.rda && (
          <div className="mb-3 text-sm py-2 px-3 bg-accent-base/5 rounded-lg">
            <span className="font-medium text-content">RDA: </span>
            <span className="text-content/80">{nutrient.rda}</span>
          </div>
        )}

        <div className="space-y-0">
          <Section id="benefits" title="Functions and Benefits">
            <ul className="list-disc list-inside text-sm text-content/80 space-y-1">
              {nutrient.benefits.map((benefit, i) => (
                <li key={i}>{benefit}</li>
              ))}
            </ul>
          </Section>
          <Section id="deficiency" title="Signs of Deficiency">
            <ul className="list-disc list-inside text-sm text-content/80 space-y-1">
              {nutrient.deficiency.map((sign, i) => (
                <li key={i}>{sign}</li>
              ))}
            </ul>
          </Section>
          <Section id="toxicity" title="Signs of Toxicity">
            <ul className="list-disc list-inside text-sm text-content/80 space-y-1">
              {nutrient.toxicity.map((sign, i) => (
                <li key={i}>{sign}</li>
              ))}
            </ul>
          </Section>
          <Section id="absorption" title="Optimal Absorption">
            <ul className="list-disc list-inside text-sm text-content/80 space-y-1">
              {nutrient.absorption.map((method, i) => (
                <li key={i}>{method}</li>
              ))}
            </ul>
          </Section>
          <Section id="sources" title="Food Sources">
            <div className="flex flex-wrap gap-2">
              {nutrient.sources.map((source, i) => (
                <span
                  key={i}
                  className="px-2 py-1 text-xs bg-accent-base/20 text-accent-text rounded-full"
                >
                  {source}
                </span>
              ))}
            </div>
          </Section>
          {nutrient.notes && (
            <Section id="notes" title="Notes">
              <p className="text-sm text-content/60 italic">{nutrient.notes}</p>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

export function Nutrition() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<'all' | 'vitamin' | 'mineral'>('all');

  const filteredNutrients = useMemo(() => {
    return nutrients.filter(nutrient => {
      const matchesSearchTerm = matchesSearch(nutrient, searchTerm);
      const matchesType = selectedType === 'all' || nutrient.type === selectedType;
      return matchesSearchTerm && matchesType;
    });
  }, [searchTerm, selectedType]);

  const letterIndex = useMemo(() => {
    const letters = new Set<string>();
    filteredNutrients.forEach(n => {
      const first = n.name.charAt(0).toUpperCase();
      letters.add(/[A-Z0-9]/.test(first) ? first : '#');
    });
    return Array.from(letters).sort((a, b) => (a === '#' ? 1 : b === '#' ? -1 : a.localeCompare(b)));
  }, [filteredNutrients]);

  const hasActiveFilters = searchTerm.trim() !== '' || selectedType !== 'all';

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedType('all');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Meta 
        title="Nutrition Information | Mindful Family"
        description="Comprehensive guide to vitamins and minerals, their benefits, and food sources."
      />

      {/* Header - same boxed style as Conversations */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-accent-text/10 rounded-lg">
              <span className="text-2xl">🥗</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-content">
                Nutrition Information
              </h1>
              <p className="text-sm text-gray-500">
                Comprehensive guide to vitamins and minerals, their benefits, and food sources.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Warning Box */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <p className="font-medium mb-1">Medical Disclaimer</p>
              <p>
                🚨 The information provided should not be used for diagnosing or treating a health problem or disease, and those seeking personal medical advice should consult with a licensed physician or Nutritionist.
              </p>
              <Link 
                to="/practitioners?category=Nutrition"
                className="mt-2 inline-flex items-center text-accent-text hover:text-accent-text/80 font-medium"
              >
                Find a Verified Nutritionist
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Filters - inside white card, not sticky */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6">
          <div className="p-4 border-b border-gray-100">
            <h2 className="font-semibold text-content">Filters</h2>
            <p className="text-xs text-gray-500 mt-1">
              Search by name, benefits, food sources, deficiency, and more
            </p>
          </div>
          <div className="p-4">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-content/40" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search nutrients..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:border-accent-text focus:ring-1 focus:ring-accent-text/20 bg-white"
                />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {(['all', 'vitamin', 'mineral'] as const).map((type) => (
                  <button
                    key={type}
                    onClick={() => setSelectedType(type)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedType === type
                        ? 'bg-accent-text text-white'
                        : 'bg-accent-base/20 text-content hover:bg-accent-base/30'
                    }`}
                  >
                    {type === 'all' ? 'All' : type === 'vitamin' ? 'Vitamins' : 'Minerals'}
                  </button>
                ))}
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-content/70 hover:text-content hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="h-4 w-4" />
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {filteredNutrients.length} {filteredNutrients.length === 1 ? 'nutrient' : 'nutrients'}
              </p>
              {letterIndex.length > 0 && (
                <div className="flex flex-wrap gap-1 justify-end">
                  {letterIndex.map((letter) => (
                    <a
                      key={letter}
                      href={`#nutrient-${letter === '#' ? 'other' : letter}`}
                      className="w-7 h-7 flex items-center justify-center text-xs font-medium text-accent-text hover:bg-accent-base/20 rounded transition-colors"
                    >
                      {letter}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* A-Z Section Headers & Nutrients Grid */}
        {filteredNutrients.length === 0 ? (
          <div className="text-center py-16 px-4 bg-white rounded-xl shadow-sm border border-gray-100">
          <p className="text-content/70 font-medium mb-2">No nutrients match your filters</p>
          <p className="text-sm text-content/60 mb-4">
            Try adjusting your search or filter to see more results.
          </p>
          <button
            onClick={clearFilters}
            className="inline-flex items-center gap-2 px-4 py-2 bg-accent-text text-white rounded-lg text-sm font-medium hover:bg-accent-text/90 transition-colors"
          >
            <X className="h-4 w-4" />
            Clear filters
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {letterIndex.map((letter) => {
            const nutrientsInLetter = filteredNutrients.filter(n => {
              const first = n.name.charAt(0).toUpperCase();
              const key = /[A-Z0-9]/.test(first) ? first : '#';
              return key === letter;
            });
            if (nutrientsInLetter.length === 0) return null;
            return (
              <section key={letter} id={`nutrient-${letter === '#' ? 'other' : letter}`} className="scroll-mt-24">
                <h2 className="text-lg font-semibold text-content mb-4 flex items-center gap-2">
                  <span className="w-10 h-10 flex items-center justify-center bg-accent-base/20 text-accent-text rounded-lg font-bold">
                    {letter}
                  </span>
                  {letter}
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {nutrientsInLetter.map((nutrient) => (
                    <NutrientCard key={nutrient.name} nutrient={nutrient} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}